import { CurrencyHelper } from './CurrencyHelper';
import { get } from '@/services/http';

/**
 * Where a resolved PO unit cost came from.
 * - 'supplier'  : SupplierProduct.cost — already in the supplier's currency.
 * - 'converted' : Shopify store-currency cost converted to supplier currency (an
 *                 estimate — surface an "(estimated)" indicator to the buyer).
 * - 'shopify'   : Shopify cost used as-is (same-currency PO, or no rate available).
 */
export type CostSource = 'supplier' | 'converted' | 'shopify';

export interface ResolvedCost {
  cost: number;
  source: CostSource;
}

/**
 * US-CURR-013 — the single supplier-cost ladder shared by every PO-creation flow.
 *
 * A PO is denominated in the supplier's currency, so its line `unitPrice` must be
 * in supplier currency. The two available cost sources are in DIFFERENT currencies:
 *   - SupplierProduct.cost            -> supplier currency (authoritative, negotiated)
 *   - Shopify [average_unit_cost] etc -> store currency
 * Feeding a store-currency cost straight into a supplier-currency PO mislabels the
 * line and corrupts the backend-derived `*_base` totals (which multiply by the rate).
 *
 * Ladder (extracted verbatim from US-CURR-009's PurchaseOrderCreate.vue implementation):
 *   1. supplier cost present  -> use as-is, never convert
 *   2. foreign PO + rate      -> shopifyCost / exchangeRate  (store -> supplier)
 *   3. otherwise              -> shopifyCost as-is
 *
 * @param supplierCost         SupplierProduct.cost in supplier currency, or null/undefined.
 * @param shopifyCost          Shopify cost in STORE currency.
 * @param exchangeRate         Supplier -> store rate (store units per 1 supplier unit).
 * @param isForeignCurrency    supplier currency !== store currency.
 * @param supplierCurrencyCode Used for currency-aware rounding (JPY 0dp, KWD 3dp, ...).
 */
export function resolveUnitCost(
  supplierCost: number | null | undefined,
  shopifyCost: number,
  exchangeRate: number | null | undefined,
  isForeignCurrency: boolean,
  supplierCurrencyCode: string = 'USD',
): ResolvedCost {
  if (supplierCost != null && supplierCost > 0) {
    return { cost: supplierCost, source: 'supplier' };
  }
  if (isForeignCurrency && exchangeRate && exchangeRate > 0 && shopifyCost > 0) {
    return {
      cost: CurrencyHelper.roundForCurrency(shopifyCost / exchangeRate, supplierCurrencyCode),
      source: 'converted',
    };
  }
  return { cost: shopifyCost || 0, source: 'shopify' };
}

/** variantId -> SupplierProduct.cost (supplier currency); null when the row has no cost. */
export type SupplierCostMap = Map<number, number | null>;

/**
 * Fetch a supplier's variantId -> cost map, ready for step 1 of the ladder.
 *
 * Both sides of the lookup MUST be Number()-coerced: Sequelize returns BIGINT
 * (variantId) and DECIMAL (cost) as STRINGS, so a raw `map.get(variant_id)` misses
 * every time. That contract is the whole reason this lives in one place — it was
 * previously re-derived in three components, and step 1 of the ladder was dead in
 * production for months without a single test noticing.
 *
 * Never throws: on failure the caller gets an empty map and the ladder falls through
 * to the Shopify cost, which is the pre-existing behaviour.
 */
export async function fetchSupplierCostMap(supplierId: number | string): Promise<SupplierCostMap> {
  try {
    return buildSupplierCostMap(await get(`/supplier-products/supplier/${supplierId}/costs`));
  } catch (err) {
    console.error('Error fetching supplier cost map:', err);
    return new Map<number, number | null>();
  }
}

/**
 * Parse the endpoint's `{ data: [{ variantId, cost }] }` body into a cost map.
 *
 * Split out from the fetch so the response-shape contract is directly testable
 * without stubbing HTTP — that boundary is exactly where step 1 of the ladder
 * silently died in production, so it needs a test that pins the real parser
 * rather than a copy of it.
 */
export function buildSupplierCostMap(response: any): SupplierCostMap {
  const items: any[] = response?.data || [];
  return new Map<number, number | null>(
    items.map((item: any) => [
      Number(item.variantId),
      item.cost != null ? Number(item.cost) : null,
    ]),
  );
}

/**
 * Fetch the supplier -> store exchange rate (store units per 1 supplier unit).
 * Returns null when unavailable, which makes the ladder fall through to step 3
 * rather than guess at a rate.
 */
export async function fetchExchangeRate(
  fromCurrency: string,
  toCurrency: string,
): Promise<number | null> {
  if (!fromCurrency || !toCurrency || fromCurrency === toCurrency) return null;
  try {
    const response: any = await get(`/exchange-rates?from=${fromCurrency}&to=${toCurrency}`);
    return response?.rate || null;
  } catch (err) {
    console.error('Error fetching exchange rate:', err);
    return null;
  }
}
